import type Parse from 'parse'

/**
 * The Profile's stored `playerConfig` when it is a plain object, else `null`.
 *
 * Null, a string and an array all count as "nothing to keep": the database
 * cannot set a nested key inside any of them, so a dotted write onto one is
 * rejected.
 */
export function readStoredPlayerConfig(profile: Parse.Object): Record<string, any> | null {
  const stored = profile.get('playerConfig')
  return stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : null
}

/**
 * Set one top-level key of `Profile.playerConfig` without sending the rest.
 *
 * Several writers share the field (the background choice in `media`, the room
 * settings in `roomConfigs`, the cloud's defaults). Sending the whole field back
 * puts the copy read a moment ago over whatever another writer saved since, on
 * every device. The dotted key puts only this key in the request, so Parse Server
 * leaves the others as stored.
 *
 * With no stored object there is nothing to keep, and the whole field is written
 * instead: dot notation onto a missing or non-object parent is not something to
 * rely on from every Parse Server version.
 *
 * The key must be a plain identifier: the SDK rejects a dotted key with any
 * character outside [A-Za-z0-9_.].
 */
export function setPlayerConfigKey(profile: Parse.Object, key: string, value: unknown): void {
  if (readStoredPlayerConfig(profile)) {
    profile.set(`playerConfig.${key}`, value)
  } else {
    profile.set('playerConfig', { [key]: value })
  }
}
